import { memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import SearchIcon from "@mui/icons-material/Search";

import { palette } from "../../theme/palette";

import { LOCATIONS_PLAN_TYPE } from "../../consts/planTypes";
import { POSTCODE } from "../../consts/layers";
import { SEARCH } from "../../consts/sentryIds";
import {
  DRIVER,
  SECTION,
  ROUTE,
  SHIFT_MANAGER,
  LOCATION,
} from "../../consts/strings";
import {
  DRIVER_MANAGEMENT,
  PLAN_MANAGEMENT,
  ROUTE_MANAGEMENT,
  SECTION_MANAGEMENT,
  SHIFT_MANAGER_MANAGEMENT,
} from "../../consts/routes";

import { Postcode } from "../../utils/postcode";

import {
  getMatrixProfileType,
  routeOptionsSelector,
  postcodesOptionsSelector,
  locationOptionsSelector,
} from "../../reducers/matrixProfile/selectors";
import { processLocationEditAction } from "../../reducers/matrixProfile/thunkActions";
import { driverOptionsSelector } from "../../reducers/driverManagement/selectors";
import { sectionOptionsSelector } from "../../reducers/sectionManagement/selectors";
import { routeOptionsSelector as routeManagementOptionsSelector } from "../../reducers/routeManagement/selectors";
import { shiftManagersOptionsSelector } from "../../reducers/shiftManagerManagement/selectors";
import {
  postcodeClickHandler,
  searchHandler,
} from "../../reducers/ownApp/thunkActions";

import { View } from "../View";
import { VirtualizedAutocomplete } from "./VirtualizedAutocomplete";

const useStyles = makeStyles({
  container: {
    marginLeft: "20px",
    alignItems: "center",
    display: "flex",
  },
  searchIcon: {
    color: palette.typography.white.high,
  },
});

const applicationsDefaultType = {
  [PLAN_MANAGEMENT]: POSTCODE,
  [DRIVER_MANAGEMENT]: DRIVER,
  [SECTION_MANAGEMENT]: SECTION,
  [ROUTE_MANAGEMENT]: ROUTE,
  [SHIFT_MANAGER_MANAGEMENT]: SHIFT_MANAGER,
};

export const SearchBar = memo(({ application }) => {
  const planType = useSelector(getMatrixProfileType);
  const locationOptions = useSelector(locationOptionsSelector);
  const postcodeOptions = useSelector(postcodesOptionsSelector);
  const routeOptions = useSelector(routeOptionsSelector);
  const routeManagementOptions = useSelector(routeManagementOptionsSelector);
  const driverOptions = useSelector(driverOptionsSelector);
  const sectionOptions = useSelector(sectionOptionsSelector);
  const shiftManagersOptions = useSelector(shiftManagersOptionsSelector);
  let [type, setType] = useState(applicationsDefaultType[application]);
  const dispatch = useDispatch();
  const styles = useStyles();

  let listOptions;
  let typeOptions;
  if (application === DRIVER_MANAGEMENT) {
    listOptions = driverOptions;
    typeOptions = [DRIVER];
  } else if (
    application === PLAN_MANAGEMENT &&
    planType === LOCATIONS_PLAN_TYPE
  ) {
    typeOptions = [LOCATION, ROUTE];
    if (!typeOptions.includes(type)) [type] = typeOptions;
    listOptions = type === LOCATION ? locationOptions : routeOptions;
  } else if (application === PLAN_MANAGEMENT) {
    listOptions = type === POSTCODE ? postcodeOptions : routeOptions;
    typeOptions = [POSTCODE, ROUTE];
  } else if (application === SECTION_MANAGEMENT) {
    listOptions = sectionOptions;
    typeOptions = [SECTION];
  } else if (application === ROUTE_MANAGEMENT) {
    listOptions = routeManagementOptions;
    typeOptions = [ROUTE];
  } else if (application === SHIFT_MANAGER_MANAGEMENT) {
    listOptions = shiftManagersOptions;
    typeOptions = [SHIFT_MANAGER];
  }

  const onChange = (value) => {
    dispatch(searchHandler({ item: value, type, application }));
  };

  const onEdit = ({ locationCode, value: postcode }) => {
    if (locationCode) dispatch(processLocationEditAction({ locationCode }));
    else {
      const level = new Postcode(postcode).getLevel();
      dispatch(postcodeClickHandler({ postcode, level, application }));
    }
  };

  return (
    <View className={styles.container}>
      <VirtualizedAutocomplete
        sentryId={`${SEARCH}-${type}`}
        options={listOptions}
        onChange={onChange}
        type={type}
        setType={setType}
        typeOptions={typeOptions}
        onEdit={onEdit}
        application={application}
      />
      <SearchIcon className={styles.searchIcon} />
    </View>
  );
});
